import React from "react";
import { FormControl, FormLabel, Input, FormHelperText } from "@mui/joy";

interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Phone number input with formatting and validation
 */
export function PhoneNumberInput({
  value,
  onChange,
  label = "Phone Number",
  placeholder = "Enter 10-digit phone number",
  error,
  disabled = false,
  required = false,
}: PhoneNumberInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;

    // Remove non-digits
    const digitsOnly = input.replace(/\D/g, "");

    // Limit to 10 digits
    if (digitsOnly.length <= 10) {
      onChange(digitsOnly);
    }
  };

  // Format for display with spacing (XXX XXX XXXX)
  const formatDisplay = (digits: string) => {
    if (!digits) return "";
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  };

  return (
    <FormControl>
      <FormLabel>
        {label}
        {required && <span style={{ color: "red" }}> *</span>}
      </FormLabel>
      <Input
        placeholder={placeholder}
        value={formatDisplay(value)}
        onChange={handleChange}
        disabled={disabled}
        inputMode="numeric"
        slotProps={{
          input: {
            maxLength: 12, // Formatted length with spaces
          },
        }}
      />
      {error && <FormHelperText sx={{ color: "danger.500" }}>{error}</FormHelperText>}
      {value && (
        <FormHelperText>
          {value.length}/10 digits
        </FormHelperText>
      )}
    </FormControl>
  );
}
